import { FolderIcon } from "@heroicons/react/24/outline";
import { isReportGoalVisible } from "@utils/reportVisibility";
import { getGoalStartIndices, unwrapConnection } from "@utils/utils";
import { useMemo } from "react";

import { Paragraph, Small } from "@fronterahealth/frontera-ui-components";

import {
  ReportGoalType,
  useGetReassessmentReportGoalsQuery,
  useGetReportDomainsQuery,
  useTreatmentPlanTemplateQuery,
} from "@api/graphql/types-and-hooks";
import { TemplateGoalReviewCard } from "@components/TreatmentPlanFieldForm/TemplateGoalReviewCard";
import { useTreatmentPlanTemplate } from "@components/TreatmentPlanFieldForm/useTreatmentPlanTemplate";
import { getGoalsGroupedByDomain } from "@pages/ClinicalReportDetails/ReportPage/utils/sortGoalsByDomain";
import { useReport } from "@providers/ReportProvider";

/**
 * RA review/completed treatment plan. Groups goals by domain to match the IA
 * review layout (`FieldComponent/TreatmentPlanField`), the editing sidebar, and
 * the domain-grouped export — while numbering goals continuously across domains
 * (1..N, not restarting per domain) and lettering targets within each goal
 * (1A/1B). Domain headers are purely visual (no `data-sticky-nav-header`, which
 * the clinical StickyNav would otherwise pick up as section entries).
 */
export const FlatTreatmentPlanGoals = () => {
  const { report } = useReport();

  const treatmentPlanTemplateQuery = useTreatmentPlanTemplateQuery(
    {
      reportId: report?.id || "",
    },
    {
      enabled: !!report?.id,
      retry: false,
    },
  );
  const treatmentPlanTemplate = treatmentPlanTemplateQuery?.data?.treatmentPlanTemplate;
  const isGasEnabled = treatmentPlanTemplate?.isGasEnabled || false;
  const { goalFields, targetFields: templateTargetFields } = useTreatmentPlanTemplate(report?.id);

  const reassessmentGoalsQuery = useGetReassessmentReportGoalsQuery(
    {
      filter: {
        reportId: report?.id,
      },
    },
    {
      queryKey: ["get-reassessment-goals", report?.id],
      enabled: !!report?.id,
    },
  );

  const { data: reassessmentGoals, refetch: reassessmentGoalsRefetch } = reassessmentGoalsQuery;

  const { data: reportDomainsData, isPending: domainsPending } = useGetReportDomainsQuery(
    { reportId: report?.id || "" },
    { enabled: !!report?.id },
  );

  const goalList: ReportGoalType[] = useMemo(
    () => (unwrapConnection(reassessmentGoals?.getReassessmentReportGoals) as ReportGoalType[]) || [],
    [reassessmentGoals?.getReassessmentReportGoals],
  );

  // Group by domain to match the editing sidebar and the export. Only non-empty
  // groups are shown; uncategorized/orphan goals (domain === null) render without
  // a header so nothing silently drops off the report.
  const domainGroups = useMemo(
    () =>
      getGoalsGroupedByDomain(goalList.filter(isReportGoalVisible), reportDomainsData?.getReportDomains ?? []).filter(
        (group) => group.goals.length > 0,
      ),
    [goalList, reportDomainsData],
  );

  // Wait for both queries before grouping — before the domains resolve, every
  // goal falls into the orphan bucket and briefly renders under an "Unknown
  // Domain" header.
  if (domainsPending || reassessmentGoalsQuery.isPending) {
    return null;
  }

  if (domainGroups.length === 0) {
    return <Small>No goals included in report.</Small>;
  }

  const handleGoalMutationSuccess = () => {
    reassessmentGoalsRefetch();
  };

  // Continuous goal numbering across domains: each group starts where the
  // previous one's goals ended.
  const groupStartIndices = getGoalStartIndices(domainGroups.map((group) => group.goals.length));

  return (
    <div className="flex flex-col gap-y-6 pt-4">
      {domainGroups.map((group, groupIdx) => {
        const startIndex = groupStartIndices[groupIdx];
        return (
          <div key={group.domainId}>
            {group.domain && (
              <div className="mb-4 flex items-center gap-x-2">
                <FolderIcon className="h-5 w-5 text-text-secondary" />
                <Paragraph displayType="loud" colorType="primary">
                  {group.domainName}
                </Paragraph>
              </div>
            )}
            <div className="flex w-full flex-col space-y-2 items-center border border-limestone-100 rounded-2xl bg-limestone-50 p-2">
              {group.goals.map((goal, i) => (
                <TemplateGoalReviewCard
                  key={goal.id}
                  goal={goal}
                  domain={goal.reportDomain ?? null}
                  report={report}
                  goalFields={goalFields}
                  targetFields={templateTargetFields}
                  isGasEnabled={isGasEnabled}
                  showChart
                  showMedicalNecessity
                  onGoalMutationSuccess={handleGoalMutationSuccess}
                  goalIndex={startIndex + i}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
